import AddIcon from "@mui/icons-material/Add";
import { Button, Stack } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { setInputs } from "../../features/Booking-Form/slices/booking.slice";
import {
  BookingType,
  NotSerializedBookingInputs,
  VisitorRegistrationEntry
} from "../../features/Booking-Form/typings/booking-inputs";
import {
  bookingTypeFromVisitorRegistrations,
  buildVisitorUsersBookedFor,
  createVisitorRegistrationEntry,
  getDuplicateVisitorEmailIds
} from "../../features/Booking-Form/functions/visitor-registration.functions";
import { VisitorRegistrationRow } from "./visitor-registration-row.component";

/** Stable fallback so `useSelector` does not return a new `[]` every render when registrations are missing. */
const emptyVisitorRegistrations: VisitorRegistrationEntry[] = [];

export function FormVisitorRegistration() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mode = useSelector((state: RootState) => state.booking.inputs.mode);
  const bookingType = useSelector((state: RootState) => state.booking.inputs.bookingType);
  const visitorRegistrations = useSelector(
    (state: RootState) => state.booking.inputs.visitorRegistrations ?? emptyVisitorRegistrations
  );
  const { sub, firstName, surname, email } = useSelector(
    (state: RootState) => state.login.userInformation
  );
  const duplicateEmailIds = getDuplicateVisitorEmailIds({
    visitorRegistrations
  } as NotSerializedBookingInputs);

  useEffect(() => {
    if (visitorRegistrations.length === 0) {
      dispatch(setInputs({ visitorRegistrations: [createVisitorRegistrationEntry()] }));
    }
  }, [dispatch, visitorRegistrations.length]);

  useEffect(() => {
    const derived = bookingTypeFromVisitorRegistrations({
      visitorRegistrations
    } as NotSerializedBookingInputs);
    if (derived != null && bookingType !== derived) {
      dispatch(setInputs({ bookingType: derived }));
      return;
    }
    if (derived == null && mode === BookingType.TEAM && bookingType !== BookingType.WORKPLACE) {
      dispatch(setInputs({ bookingType: BookingType.WORKPLACE }));
    }
    if (derived == null && mode === BookingType.VISITOR && bookingType !== BookingType.VISITOR) {
      dispatch(setInputs({ bookingType: BookingType.VISITOR }));
    }
  }, [dispatch, mode, bookingType, visitorRegistrations]);

  useEffect(() => {
    if (mode !== BookingType.VISITOR || visitorRegistrations.length === 0) {
      return;
    }
    dispatch(
      setInputs({
        usersBookedFor: buildVisitorUsersBookedFor(
          { sub, firstName, surname, email },
          visitorRegistrations
        )
      })
    );
  }, [dispatch, mode, visitorRegistrations, sub, firstName, surname, email]);

  const patchVisitor = useCallback(
    (id: string, patch: Partial<VisitorRegistrationEntry>) => {
      const next = visitorRegistrations.map(v => (v.id === id ? { ...v, ...patch } : v));
      dispatch(setInputs({ visitorRegistrations: next }));
    },
    [dispatch, visitorRegistrations]
  );

  const addVisitor = useCallback(() => {
    const first = visitorRegistrations[0];
    const newEntry: VisitorRegistrationEntry = {
      ...createVisitorRegistrationEntry(),
      company: (first?.company ?? "").trim(),
      visitReason: (first?.visitReason ?? "").trim()
    };
    dispatch(
      setInputs({
        visitorRegistrations: [...visitorRegistrations, newEntry]
      })
    );
  }, [dispatch, visitorRegistrations]);

  const removeVisitor = useCallback(
    (id: string) => {
      if (visitorRegistrations.length <= 1) {
        return;
      }
      dispatch(setInputs({ visitorRegistrations: visitorRegistrations.filter(v => v.id !== id) }));
    },
    [dispatch, visitorRegistrations]
  );

  return (
    <Stack spacing={3} sx={{ py: 2, px: 0 }} data-testid="visitor-registration-form">
      <Stack spacing={2}>
        {visitorRegistrations.map((entry, index) => (
          <VisitorRegistrationRow
            key={entry.id}
            canRemove={visitorRegistrations.length > 1}
            emailDuplicate={duplicateEmailIds.has(entry.id)}
            entry={entry}
            index={index}
            onPatch={patchVisitor}
            onRemove={removeVisitor}
          />
        ))}
      </Stack>

      <Button
        color="primary"
        data-testid="visitor-registration-add"
        onClick={addVisitor}
        startIcon={<AddIcon />}
        sx={{
          alignSelf: "flex-start",
          borderRadius: 2,
          fontWeight: 600,
          px: 2.5,
          textTransform: "none"
        }}
        variant="outlined"
      >
        {t("_visitorBooking_addVisitor")}
      </Button>
    </Stack>
  );
}
